import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, errMsg, BOAT_CATEGORIES, ANNUNCIO_CATEGORIE, MAX_FILE, slug } from "@/lib/api";

const EMPTY = { tipo: "vendo", categoria: "", categoria_barca: "none", titolo: "", descrizione: "", prezzo: "", luogo: "" };

const SimpleSelect = ({ value, onChange, options, testid, placeholder }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      {options.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`${testid}-option-${slug(v)}`}>{l}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const AnnuncioForm = ({ open, onOpenChange, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setF(EMPTY);
      setFiles([]);
    }
  }, [open]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const pickFiles = (e) => {
    const list = Array.from(e.target.files || []);
    if (list.length > 3) return toast.error("Massimo 3 foto");
    if (list.some((x) => !/\.(jpe?g)$/i.test(x.name))) return toast.error("Sono ammesse solo foto JPG");
    if (list.some((x) => x.size > MAX_FILE)) return toast.error("Ogni foto deve essere al massimo di 2 MB");
    setFiles(list);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.categoria) return toast.error("Seleziona la categoria");
    setSaving(true);
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => {
      if (k === "categoria_barca" && v === "none") return;
      fd.append(k, v);
    });
    files.forEach((x) => fd.append("foto", x));
    try {
      await api.post("/annunci", fd);
      toast.success("Annuncio pubblicato per 30 giorni");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="annuncio-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl uppercase">Nuovo annuncio</DialogTitle>
        </DialogHeader>
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-lg p-4 text-sm leading-relaxed" data-testid="annuncio-form-note">
          Possono pubblicare annunci gli utenti registrati. Gli annunci rimarranno online per 30 giorni.
          In prossimità della scadenza, l'utente che ha pubblicato l'annuncio verrà informato e invitato eventualmente a rinnovarne la pubblicazione.
          Non vengono mostrati email o telefono. Le persone interessate ad un annuncio potranno rispondere attraverso la messaggistica del sito.
          L'inserzionista riceverà una notifica nell'area riservata.
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div><Label>Tipo</Label><SimpleSelect value={f.tipo} onChange={(v) => setF({ ...f, tipo: v })} options={[["vendo", "Vendo"], ["compro", "Compro"]]} testid="annuncio-type-select" /></div>
            <div><Label>Categoria</Label><SimpleSelect value={f.categoria} onChange={(v) => setF({ ...f, categoria: v })} options={ANNUNCIO_CATEGORIE.map((c) => [c, c])} testid="annuncio-category-select" placeholder="Seleziona" /></div>
            <div><Label>Categoria barca</Label><SimpleSelect value={f.categoria_barca} onChange={(v) => setF({ ...f, categoria_barca: v })} options={[["none", "Nessuna"], ...BOAT_CATEGORIES.map((c) => [c, c])]} testid="annuncio-boat-category-select" /></div>
          </div>
          <div><Label>Titolo</Label><Input required value={f.titolo} onChange={set("titolo")} data-testid="annuncio-title-input" /></div>
          <div><Label>Descrizione</Label><Textarea required rows={4} value={f.descrizione} onChange={set("descrizione")} data-testid="annuncio-description-input" /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label>Prezzo (€)</Label><Input required type="number" min="0" step="0.01" value={f.prezzo} onChange={set("prezzo")} data-testid="annuncio-price-input" /></div>
            <div><Label>Luogo dove è visibile</Label><Input required value={f.luogo} onChange={set("luogo")} data-testid="annuncio-location-input" /></div>
          </div>
          <div>
            <Label>Foto (max 3 JPG, 2 MB ciascuna)</Label>
            <Input type="file" accept=".jpg,.jpeg,image/jpeg" multiple onChange={pickFiles} data-testid="annuncio-photos-input" />
            {files.length > 0 && <p className="text-xs text-muted-foreground mt-1">{files.length} foto selezionate</p>}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="annuncio-form-cancel-button">Annulla</Button>
            <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="annuncio-form-submit-button">
              {saving ? "Pubblicazione…" : "Pubblica"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
